import { useState } from 'react';
import { useParams } from 'react-router-dom';
import type { Block, MediaRef, SeoMeta, TranslatableString } from '@geoic/shared';
import { Card, CardBody, CardHeader } from '@/components/ui/Card.js';
import { Checkbox, Input } from '@/components/ui/Field.js';
import { TranslatableInput } from '@/components/form/TranslatableInput.js';
import { MediaPicker } from '@/components/media/MediaPicker.js';
import { Badge } from '@/components/ui/Badge.js';
import { cn } from '@/lib/cn.js';
import { price as formatPrice } from '@/lib/format.js';
import { useAuth } from '@/features/auth/auth-context.js';
import { BlockConstructor } from '@/features/blocks/BlockConstructor.js';
import {
  EnumSelect,
  IconInput,
  Repeater,
  emptyTranslatable,
} from '@/features/blocks/BlockFields.js';
import { ContentListPage } from '../ContentListPage.js';
import { ContentFormLayout } from '../ContentFormLayout.js';
import { DraftBanner } from '../DraftBanner.js';
import { SeoPanel, emptySeo } from '../SeoPanel.js';
import { useContentForm } from '../useContentForm.js';
import { createContentApi, type ContentRecord } from '../content-api.js';

export type ServicePriceRow = {
  name: TranslatableString;
  price: number | null;
  duration: TranslatableString;
  order: number;
};

export type ServiceRecord = ContentRecord & {
  serviceType: 'main' | 'repair';
  title: TranslatableString;
  shortDescription: TranslatableString;
  icon: string | null;
  coverImage: MediaRef | null;
  blocks: Block[];
  priceList: ServicePriceRow[];
  isFeatured: boolean;
  seo: SeoMeta;
};

export const servicesApi = createContentApi<ServiceRecord>('/services');

const TYPE_LABELS = { main: 'Asosiy xizmat', repair: 'Ta’mirlash' } as const;

// ─── Ro'yxat ─────────────────────────────────────────────────────────────────

export function ServicesListPage() {
  return (
    <ContentListPage<ServiceRecord>
      title="Xizmatlar"
      collection="services"
      contentApi={servicesApi}
      basePath="/services"
      createLabel="Xizmat qo‘shish"
      emptyTitle="Hozircha xizmat yo‘q"
      columns={[
        {
          key: 'title',
          header: 'Nomi',
          render: (row) => (
            <span className="font-medium text-n-800">{row.title?.uz || '(nomsiz)'}</span>
          ),
        },
        {
          key: 'serviceType',
          header: 'Turi',
          width: '160px',
          render: (row) => (
            <Badge tone={row.serviceType === 'repair' ? 'warning' : 'brand'}>
              {TYPE_LABELS[row.serviceType ?? 'main']}
            </Badge>
          ),
        },
        {
          key: 'priceList',
          header: 'Narx qatorlari',
          align: 'right',
          width: '130px',
          hideOnTablet: true,
          render: (row) => <span className="text-n-600">{row.priceList?.length ?? 0}</span>,
        },
      ]}
    />
  );
}

// ─── Tahrirlash ──────────────────────────────────────────────────────────────

type ServiceForm = {
  title: TranslatableString;
  slug: string;
  serviceType: 'main' | 'repair';
  shortDescription: TranslatableString;
  icon: string;
  coverImage: MediaRef | null;
  blocks: Block[];
  priceList: ServicePriceRow[];
  isFeatured: boolean;
  seo: SeoMeta;
};

type Tab = 'main' | 'blocks' | 'prices';

/**
 * Xizmat tahrirlash.
 *
 * Uch yorliq: asosiy maydonlar, blok konstruktori (xizmat sahifasi ham
 * bloklardan yig'iladi) va narxlar jadvali (ta'mirlash uchun — TZ 3.7).
 */
export function ServiceEditPage() {
  const { id = 'new' } = useParams();
  const { user } = useAuth();
  const [tab, setTab] = useState<Tab>('main');

  const form = useContentForm<ServiceForm>({
    collection: 'services',
    contentApi: servicesApi,
    id,
    basePath: '/services',
    empty: () => ({
      title: emptyTranslatable(),
      slug: '',
      serviceType: 'main',
      shortDescription: emptyTranslatable(),
      icon: '',
      coverImage: null,
      blocks: [],
      priceList: [],
      isFeatured: false,
      seo: emptySeo(),
    }),
    fromRecord: (record) => {
      const service = record as ServiceRecord;
      return {
        title: service.title ?? emptyTranslatable(),
        slug: service.slug ?? '',
        serviceType: service.serviceType ?? 'main',
        shortDescription: service.shortDescription ?? emptyTranslatable(),
        icon: service.icon ?? '',
        coverImage: service.coverImage ?? null,
        blocks: service.blocks ?? [],
        priceList: service.priceList ?? [],
        isFeatured: service.isFeatured ?? false,
        seo: service.seo ?? emptySeo(),
      };
    },
    toInput: (value) => ({
      title: value.title,
      slug: value.slug,
      serviceType: value.serviceType,
      shortDescription: value.shortDescription,
      icon: value.icon || null,
      coverImage: value.coverImage,
      blocks: value.blocks,
      priceList: value.priceList.map((row, index) => ({ ...row, order: index })),
      isFeatured: value.isFeatured,
      seo: value.seo,
    }),
  });

  if (form.loading) return <p className="p-6 text-sm text-n-600">Yuklanmoqda…</p>;
  if (form.notFound) return <p className="p-6 text-sm text-error-600">Xizmat topilmadi.</p>;

  return (
    <ContentFormLayout
      title={form.form.title.uz || (form.isNew ? 'Yangi xizmat' : '(nomsiz)')}
      backTo="/services"
      backLabel="Xizmatlar"
      isNew={form.isNew}
      isPublished={form.isPublished}
      saving={form.saving}
      publishing={form.publishing}
      canPublish={user?.role !== 'editor'}
      autosave={form.autosave}
      lock={form.lock}
      unsaved={form.unsaved}
      onSave={form.save}
      onTogglePublish={form.togglePublish}
      onPreview={form.preview}
      actions={
        <div className="flex border border-n-200 p-0.5">
          {(
            [
              ['main', 'Asosiy'],
              ['blocks', `Bloklar (${form.form.blocks.length})`],
              ['prices', `Narxlar (${form.form.priceList.length})`],
            ] as const
          ).map(([key, label]) => (
            <button
              key={key}
              type="button"
              aria-pressed={tab === key}
              onClick={() => setTab(key)}
              className={cn(
                ' px-3 py-1.5 text-sm font-medium transition-colors',
                tab === key ? 'bg-brand-600 text-white' : 'text-n-600 hover:bg-n-100',
              )}
            >
              {label}
            </button>
          ))}
        </div>
      }
    >
      <div className="flex min-h-0 flex-1 flex-col gap-4 p-4 sm:p-6">
        <DraftBanner
          savedAt={form.pendingDraft ? (form.record?.draftAt ?? null) : null}
          onRestore={form.restoreDraft}
          onDiscard={form.discardDraft}
        />

        {tab === 'blocks' && (
          <BlockConstructor
            blocks={form.form.blocks}
            role={user?.role ?? 'editor'}
            onChange={(blocks) => form.setForm((current) => ({ ...current, blocks }))}
          />
        )}

        {tab === 'main' && (
          <div className="grid gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader title="Asosiy" />
              <CardBody className="space-y-4">
                <TranslatableInput
                  label="Nomi"
                  required
                  value={form.form.title}
                  onChange={(title) => form.setForm((current) => ({ ...current, title }))}
                  error={form.fieldErrors['title.uz']}
                />
                <EnumSelect
                  label="Xizmat turi"
                  value={form.form.serviceType}
                  options={[
                    { value: 'main', label: 'Asosiy xizmat' },
                    { value: 'repair', label: 'Ta’mirlash va texnik xizmat' },
                  ]}
                  onChange={(serviceType) =>
                    form.setForm((current) => ({ ...current, serviceType }))
                  }
                />
                <TranslatableInput
                  label="Qisqa tavsif"
                  multiline
                  rows={3}
                  value={form.form.shortDescription}
                  onChange={(shortDescription) =>
                    form.setForm((current) => ({ ...current, shortDescription }))
                  }
                />
                <MediaPicker
                  label="Muqova rasmi"
                  value={form.form.coverImage}
                  onChange={(coverImage) => form.setForm((current) => ({ ...current, coverImage }))}
                />
                <IconInput
                  value={form.form.icon}
                  onChange={(icon) => form.setForm((current) => ({ ...current, icon }))}
                />
                <Input
                  label="Manzil (slug)"
                  value={form.form.slug}
                  onChange={(event) =>
                    form.setForm((current) => ({ ...current, slug: event.target.value }))
                  }
                  error={form.fieldErrors.slug}
                />
                <Checkbox
                  label="Tanlangan xizmat"
                  checked={form.form.isFeatured}
                  onChange={(event) =>
                    form.setForm((current) => ({ ...current, isFeatured: event.target.checked }))
                  }
                />
              </CardBody>
            </Card>

            <SeoPanel
              value={form.form.seo}
              onChange={(seo) => form.setForm((current) => ({ ...current, seo }))}
              fallbackTitle={form.form.title.uz || undefined}
            />
          </div>
        )}

        {tab === 'prices' && (
          <Card>
            <CardHeader
              title="Narxlar jadvali"
              description="Ta’mirlash xizmatlari uchun. Narx bo‘sh qoldirilsa saytda «Narxni so‘rang» chiqadi"
            />
            <CardBody>
              <Repeater
                label="Qatorlar"
                max={40}
                addLabel="Qator qo‘shish"
                items={form.form.priceList}
                getId={(_, index) => `price-${index}`}
                summary={(row, index) =>
                  `${row.name.uz || `${index + 1}-qator`} — ${formatPrice(row.price)}`
                }
                create={() => ({
                  name: emptyTranslatable(),
                  price: null,
                  duration: emptyTranslatable(),
                  order: form.form.priceList.length,
                })}
                onChange={(priceList) => form.setForm((current) => ({ ...current, priceList }))}
                renderItem={(row, update) => (
                  <>
                    <TranslatableInput
                      label="Ish nomi"
                      value={row.name}
                      onChange={(name) => update({ ...row, name })}
                    />
                    <Input
                      type="number"
                      label="Narx (so‘m)"
                      min={0}
                      value={row.price ?? ''}
                      placeholder="Bo‘sh — «Narxni so‘rang»"
                      onChange={(event) =>
                        update({
                          ...row,
                          price: event.target.value === '' ? null : Number(event.target.value),
                        })
                      }
                      hint="0 kiritilsa saytda «Bepul» deb ko‘rsatiladi"
                    />
                    <TranslatableInput
                      label="Muddat"
                      value={row.duration}
                      onChange={(duration) => update({ ...row, duration })}
                    />
                  </>
                )}
              />
            </CardBody>
          </Card>
        )}
      </div>
    </ContentFormLayout>
  );
}
